import React, { useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { HbrDrawer, HbrDivider, HbrSpinner } from '@harbor/elements-react-standalone';
import ProductTagManager from '../Common/ProductTagManager';
import CustomerApplicationForm from '../Customer/CustomerApplicationForm';
import CustomerSiteForm from '../Customer/CustomerSiteForm';
import { useFetchCustomer } from '../../hooks/customer-hooks';
import CustomerContactsForm from '../Common/CustomerContactsForm';
import { useFetchUser } from '../../hooks/user-hooks';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../support/store';
import { setOpportunityUiValue } from '../../support/slices/uiSlice';

// Toolbox-powered components
import CustomerPriority from '../Customer/CustomerPriority';
import CustomerChallenge from '../Customer/CustomerChallenge';
import CustomerCompliance from '../Customer/CustomerCompliance';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity } from '../../hooks/opportunity-hooks';
import StbButton from '../security-toolbox-ui/StbButton';


const KeyValuePairs = ({ customer }) => (
  <dl className="hbr-css__layout-pairs-row gap-x-2 gap-y-1 grid grid-cols-2">
    <dt className="hbr-css__text-primary-md-semibold">Size</dt>
    <dd className="hbr-css__text-primary-md">{customer.company_size}</dd>
    <dt className="hbr-css__text-primary-md-semibold">Location</dt>
    <dd className="hbr-css__text-primary-md">{customer.city}, {customer.country}</dd>
    <dt className="hbr-css__text-primary-md-semibold">Revenue</dt>
    <dd className="hbr-css__text-primary-md">{customer.AnnualRevenue}</dd>
  </dl>
);

const OppCustomerCard: React.FC = () => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const accountId = useMemo(() => opportunity?.AccountId ?? opportunity?.customer_id, [opportunity]);

  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerVisible = useSelector((state: RootState) => state.ui.showOpportunityDrawer);

  const { data: user } = useFetchUser();
  const { data: customer } = useFetchCustomer(accountId, { enabled: !!accountId });

  const toggleDrawer = () => {
    dispatch(setOpportunityUiValue({
      property: 'showOpportunityDrawer',
      value: !drawerVisible,
    }));
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <div className="h-full">
      <StbButton loading={!customer || !user} onClick={toggleDrawer} className="hbr-button-group__button hbr-button-group__button--first">
        Customer Details
      </StbButton>
      <div ref={drawerRef}>
        <HbrDrawer open={drawerVisible} id="customer-details-drawer" onHbr-hide={toggleDrawer} scrollAware={true} overlay={true} size="medium">
          <div className="sticky top-0 bg-white z-50 pl-2">
            <h2 className="mt-0 mb-6">
              {customer?.Name}
            </h2>
          </div>
          <div className="drawer-body pl-2 pr-4" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
            {customer?.Id != null ? (
              <div>
                <KeyValuePairs customer={customer} />
                <HbrDivider />

                <ProductTagManager
                  title="Cisco Solutions"
                  isCisco={true}
                  customerId={customer?.Id}
                  drawerView={true}
                  editable={false}
                  productKind={'customer'}
                />

                <ProductTagManager
                  title="Competitive Solutions"
                  isCisco={false}
                  customerId={customer?.Id}
                  drawerView={true}
                  editable={false}
                  productKind={'customer'}
                />

                <CustomerContactsForm customer={customer} />
                <HbrDivider />

                <CustomerApplicationForm customerId={customer?.Id} />
                <HbrDivider />

                <CustomerSiteForm customerId={customer?.Id} />
                <HbrDivider />

                <CustomerPriority customerID={customer?.Id} />
                <HbrDivider />

                <CustomerChallenge customerID={customer?.Id} />
                <HbrDivider />

                <CustomerCompliance customerID={customer?.Id} />
              </div>
            ) : (
              <HbrSpinner size="small" />
            )}
          </div>
        </HbrDrawer>
      </div>
    </div>
  );
};

export default OppCustomerCard;